import { format } from "date-fns";
import { useRef, useState } from "react";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";

import { useScreenSize } from "../../hooks/useScreenSize";
import "react-datepicker/dist/react-datepicker.css";
import { CalendarIcon2 } from "../../icons/CalendarIcon2/CalendarIcon2";
import { FilterIcon } from "../../icons/FilterIcon/FilterIcon";
import { MonitorTypeItem } from "../../pages/Customer/Dashboard/Monitors";
import { AddButton } from "../AddButton/AddButton";
import { ButtonWidth, CustomButton } from "../CustomButton/CustomButton";
import { SubMenuButton } from "../SubMenuButton/SubMenuButton";

import styles from "./filters.module.css";

export const getStartOfCurrentYear = () => {
  const now = new Date();
  return new Date(now.getFullYear(), 0, 1);
};

type FiltersProps = {
  fromDate?: Date | null;
  toDate?: Date | null;
  areas?: string[];
  appointmentTypesOptions?: any[];
  types?: string[];
  propertyAreaOptions?: any[];
  stationarOptions?: any[];
  selectedStationarOptions?: string[];
  monitorTypesOptions?: MonitorTypeItem[];
  monitorTypes?: string[];
  statusesOptions?: any[];
  selectedStatusesOptions?: string[];
  trapTypesOptions?: any[];
  selectedTrapTypesOptions?: string[];
  selected?: boolean;
  onClose: () => void;
  onApplyFilter: (
    fromDate: string,
    toDate: string,
    propertyAreas: string[],
    appointmentTypes: string[],
    selectedStationar: string[],
    selectedMonitorTypes: string[],
    selectedCheckbox: boolean,
    selectedStatuses: string[],
    selectedTrapTypes: string[]
  ) => void;
  showDateFilter?: boolean;
  showStationarFilter?: boolean;
  showAppointmentTypesFilter?: boolean;
  showAreaFilter?: boolean;
  showMonitorTypesFilter?: boolean;
  showStatusesFilter?: boolean;
  buttonWidth: ButtonWidth;
  showCheckbox?: boolean;
};

export const Filters = ({
  fromDate = null,
  toDate = null,
  areas = [],
  appointmentTypesOptions = [],
  types = [],
  propertyAreaOptions = [],
  stationarOptions = [],
  selectedStationarOptions = [],
  monitorTypesOptions = [],
  monitorTypes = [],
  statusesOptions = [],
  selectedStatusesOptions = [],
  trapTypesOptions = [],
  selectedTrapTypesOptions = [],
  selected = false,
  onClose,
  onApplyFilter,
  showDateFilter = false,
  showAreaFilter = false,
  showAppointmentTypesFilter = false,
  showStationarFilter = false,
  showMonitorTypesFilter = false,
  showStatusesFilter = false,
  buttonWidth = 60,
  showCheckbox = false,
}: FiltersProps) => {
  const { t } = useTranslation();
  const { isDesktop } = useScreenSize();

  const fromPickerRef = useRef<any>(null);
  const toPickerRef = useRef<any>(null);
  const [localFromDate, setLocalFromDate] = useState<Date | null>(
    fromDate ?? getStartOfCurrentYear()
  );
  const [localToDate, setLocalToDate] = useState<Date | null>(toDate);
  const [selectedAppointmentTypes, setSelectedAppointmentTypes] =
    useState<string[]>(types);
  const [selectedAreas, setSelectedAreas] = useState<string[]>(areas);
  const [selectedStationar, setSelectedStationar] = useState<string[]>(
    selectedStationarOptions
  );
  const [selectedMonitorTypes, setSelectedMonitorTypes] = useState<string[]>(
    monitorTypes || []
  );
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(
    selectedStatusesOptions || []
  );
  const [selectedTrapTypes, setSelectedTrapTypes] = useState<string[]>(
    selectedTrapTypesOptions || []
  );
  const [selectedCheckbox, setSelectedCheckbox] = useState(selected);

  const handleResetFilter = () => {
    setLocalFromDate(null);
    setLocalToDate(null);
    setSelectedAreas([]);
    setSelectedAppointmentTypes([]);
    setSelectedStationar([]);
    setSelectedMonitorTypes([]);
    setSelectedStatuses([]);
    setSelectedTrapTypes([]);
    setSelectedCheckbox(false);
  };

  const handleApplyFilter = () => {
    const formattedFromDate = localFromDate
      ? format(localFromDate, "yyyy-MM-dd")
      : "";
    const formattedToDate = localToDate
      ? format(localToDate, "yyyy-MM-dd")
      : "";

    onApplyFilter(
      formattedFromDate,
      formattedToDate,
      selectedAreas,
      selectedAppointmentTypes,
      selectedStationar,
      selectedMonitorTypes,
      selectedCheckbox,
      selectedStatuses,
      selectedTrapTypes
    );

    onClose();
  };

  const onSelectType = (type: any) => {
    setSelectedAppointmentTypes((prev) =>
      prev.includes(type.value)
        ? prev.filter((item) => item !== type.value)
        : [...prev, type.value]
    );
  };

  const onSelectArea = (areaId: string) => {
    setSelectedAreas((prev) =>
      prev.includes(areaId)
        ? prev.filter((id) => id !== areaId)
        : [...prev, areaId]
    );
  };

  const onSelectStationar = (type: any) => {
    setSelectedStationar((prev) =>
      prev.includes(type.value)
        ? prev.filter((v) => v !== type.value)
        : [...prev, type.value]
    );
  };

  const onSelectTrapType = (type: any) => {
    setSelectedTrapTypes((prev) =>
      prev.includes(type.value)
        ? prev.filter((v) => v !== type.value)
        : [...prev, type.value]
    );
  };

  const onSelectStatus = (status: any) => {
    setSelectedStatuses((prev) =>
      prev.includes(status.value)
        ? prev.filter((v) => v !== status.value)
        : [...prev, status.value]
    );
  };

  const onSelectMonitorType = (type: string) => {
    setSelectedMonitorTypes((prev) =>
      prev.includes(type)
        ? prev.filter((item) => item !== type)
        : [...prev, type]
    );
  };

  const handleCheckbox = () => {
    if (selectedCheckbox) {
      setSelectedCheckbox(false);
    } else {
      setSelectedCheckbox(true);
    }
  };

  return (
    <div className={styles["filters"]}>
      <div className={styles["filters__section"]}>
        <div className={styles["filters__header"]}>
          <p className={styles["filters__title"]}>
            {isDesktop ? (
              <>
                <FilterIcon color="dark" />
                <span>Filter</span>
              </>
            ) : (
              <>
                <CustomButton icon="filterLight" color="green" width={36} />
                <span style={{ fontWeight: 400 }}>
                  <b>Filter:</b> Analyse
                </span>
              </>
            )}
          </p>

          <span
            onClick={handleResetFilter}
            className={styles["filters__reset-text"]}
          >
            {t("ResetFilter")}
          </span>
        </div>

        {showDateFilter && (
          <>
            <span className={styles["filters__type"]}>{t("TimePeriod")}</span>
            <div className={styles["date-filter-container"]}>
              <div className={styles["filter"]}>
                <label className={styles["filter-date__label"]}>
                  {t("From")}
                </label>
                <div
                  className={styles["date-container"]}
                  onClick={() => fromPickerRef.current?.setOpen(true)}
                >
                  <DatePicker
                    ref={fromPickerRef}
                    selected={localFromDate || undefined}
                    onChange={(date: Date | null) => setLocalFromDate(date)}
                    maxDate={localToDate || undefined}
                    dateFormat="dd/MM/yyyy"
                    className={styles["custom-date-picker"]}
                    placeholderText="dd/mm/yyyy"
                    portalId="react-datepicker-portal"
                    popperPlacement="bottom-start"
                  />
                  <CalendarIcon2 />
                </div>
              </div>

              <div className={styles["filter"]}>
                <label className={styles["filter-date__label"]}>
                  {t("To")}
                </label>
                <div
                  className={styles["date-container"]}
                  onClick={() => toPickerRef.current?.setOpen(true)}
                >
                  <DatePicker
                    ref={toPickerRef}
                    selected={localToDate || undefined}
                    onChange={(date: Date | null) => setLocalToDate(date)}
                    minDate={localFromDate || undefined}
                    dateFormat="dd/MM/yyyy"
                    className={styles["custom-date-picker"]}
                    placeholderText="dd/mm/yyyy"
                    portalId="react-datepicker-portal"
                    popperPlacement="bottom-start"
                  />
                  <CalendarIcon2 />
                </div>
              </div>
            </div>
          </>
        )}

        {showStationarFilter && (
          <>
            <span className={styles["filters__type"]}>{t("TrapType")}</span>
            <div className={styles["filter-container"]}>
              {stationarOptions.map((button, i) => (
                <SubMenuButton
                  key={i}
                  currentTab={stationarOptions
                    .filter((type) => selectedStationar.includes(type.value))
                    .map((type) => type.name)}
                  value={button.name}
                  onSetTab={() => onSelectStationar(button)}
                  filter
                  multiSelect
                />
              ))}
              {trapTypesOptions.map((button, i) => (
                <SubMenuButton
                  key={i}
                  currentTab={trapTypesOptions
                    .filter((type) => selectedTrapTypes.includes(type.value))
                    .map((type) => type.name)}
                  value={button.name}
                  onSetTab={() => onSelectTrapType(button)}
                  filter
                  multiSelect
                />
              ))}
            </div>
          </>
        )}

        {showAppointmentTypesFilter && (
          <>
            <span className={styles["filters__type"]}>{t("LastServices")}</span>
            <div
              className={styles["filter-container"]}
              style={!isDesktop ? { gap: "10px" } : undefined}
            >
              {appointmentTypesOptions.length > 0 &&
                appointmentTypesOptions.map((button, i) => (
                  <SubMenuButton
                    currentTab={appointmentTypesOptions
                      .filter((type) =>
                        selectedAppointmentTypes.includes(type.value)
                      )
                      .map((type) => type.name)}
                    value={button.name}
                    onSetTab={() => onSelectType(button)}
                    key={i}
                    filter
                    multiSelect
                  />
                ))}
            </div>
          </>
        )}

        {showAreaFilter && (
          <>
            <span className={styles["filters__type"]}>{t("Floors")}</span>
            <div
              className={styles["filter-container"]}
              style={!isDesktop ? { gap: "8px" } : undefined}
            >
              {propertyAreaOptions.length > 0 &&
                propertyAreaOptions.map((area, i) => (
                  <SubMenuButton
                    key={i}
                    currentTab={propertyAreaOptions
                      .filter((type) => selectedAreas.includes(type.id))
                      .map((type) => type.name)}
                    value={area.name}
                    onSetTab={() => onSelectArea(area.id)}
                    filter
                    multiSelect
                  />
                ))}
            </div>
          </>
        )}

        {showMonitorTypesFilter && (
          <>
            <span className={styles["filters__type"]}>{t("MonitorTypes")}</span>
            <div
              className={styles["filter-container"]}
              style={!isDesktop ? { gap: "8px" } : undefined}
            >
              {monitorTypesOptions.length > 0 &&
                monitorTypesOptions.map((type, i) => (
                  <SubMenuButton
                    currentTab={monitorTypesOptions
                      .filter((type) => selectedMonitorTypes.includes(type.id))
                      .map((type) => type.label)}
                    value={type.label}
                    onSetTab={() => onSelectMonitorType(type.id)}
                    key={i}
                    filter
                    multiSelect
                  />
                ))}
            </div>
          </>
        )}

        {showStatusesFilter && (
          <>
            <span className={styles["filters__type"]}>Status</span>
            <div className={styles["filter-container"]}>
              {statusesOptions.map((button, i) => (
                <SubMenuButton
                  key={i}
                  currentTab={statusesOptions
                    .filter((type) => selectedStatuses.includes(type.value))
                    .map((type) => type.name)}
                  value={button.name}
                  onSetTab={() => onSelectStatus(button)}
                  filter
                  multiSelect
                />
              ))}
            </div>
          </>
        )}

        {showCheckbox && (
          <div className={styles["filter__checkbox"]}>
            <AddButton
              background="dark"
              iconColor="light"
              icon="check"
              onClick={handleCheckbox}
              selected={selectedCheckbox}
              iconSize={21}
            ></AddButton>
            <span
              className={`${styles["filters__type"]} ${
                selectedCheckbox && styles["filter__checkbox--checked"]
              }`}
            >
              {t("ShowOnlyInfestations")}
            </span>
          </div>
        )}
      </div>
      <div className={styles["button-wrapper"]}>
        <CustomButton
          color="green"
          onClick={handleApplyFilter}
          width={buttonWidth}
        >
          {t("Confirm")}
        </CustomButton>
      </div>
    </div>
  );
};
